import { useState, useEffect } from "react";
import { userService } from "@/services/userService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, UserCheck, UserX, Pencil, Trash2, KeyRound } from "lucide-react";

const ROLE_OPTIONS = [{ value: "admin", label: "Admin" }, { value: "manager", label: "Manager" }, { value: "agent", label: "Agent" }, { value: "end_user", label: "End User" }];

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "agent", department: "" });
  const [creating, setCreating] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", department: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState("");
  const [resetPwSaving, setResetPwSaving] = useState(false);

  const fetchUsers = async () => { try { const { data } = await userService.list(); setUsers(data); } catch { toast.error("Failed to load users"); } finally { setLoading(false); } };
  useEffect(() => { fetchUsers(); }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault(); setCreating(true);
    try { await userService.create(newUser); toast.success("User created"); setDialogOpen(false); setNewUser({ email: "", password: "", name: "", role: "agent", department: "" }); fetchUsers(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setCreating(false); }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try { await userService.update(userId, { is_active: !currentActive }); toast.success(currentActive ? "User deactivated" : "User activated"); fetchUsers(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, role: u.role, department: u.department || "" });
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const updates = {};
      if (editForm.name !== editUser.name) updates.name = editForm.name;
      if (editForm.role !== editUser.role) updates.role = editForm.role;
      if (editForm.department !== (editUser.department || "")) updates.department = editForm.department;
      if (Object.keys(updates).length === 0) { setEditUser(null); return; }
      await userService.update(editUser.id, updates);
      toast.success("User updated");
      setEditUser(null);
      fetchUsers();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setEditSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await userService.delete(deleteConfirm.id);
      toast.success("User deleted");
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleResetPassword = async () => {
    if (!resetPwUser || !resetPwValue) return;
    if (resetPwValue.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setResetPwSaving(true);
    try {
      await userService.resetPassword(resetPwUser.id, resetPwValue);
      toast.success(`Password reset for ${resetPwUser.name}. They will be required to change it on next login.`);
      setResetPwUser(null);
      setResetPwValue("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setResetPwSaving(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold tracking-tight text-[#09090B]">User Management</h1><p className="text-sm text-[#52525B] mt-0.5">{users.length} user{users.length !== 1 ? "s" : ""}</p></div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white gap-1.5" data-testid="create-user-button"><Plus size={15} /> New User</Button></DialogTrigger>
          <DialogContent className="rounded-sm border-[#E4E4E7]">
            <DialogHeader><DialogTitle className="font-heading font-bold">Create New User</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-3" data-testid="create-user-form">
              <div className="space-y-1.5"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Name</Label><Input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="rounded-sm border-zinc-300" required /></div>
              <div className="space-y-1.5"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Email</Label><Input type="email" data-testid="new-user-email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="rounded-sm border-zinc-300" required /></div>
              <div className="space-y-1.5"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Password</Label><Input type="password" data-testid="new-user-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="rounded-sm border-zinc-300" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Role</Label><Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}><SelectTrigger className="rounded-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Department</Label><Input data-testid="new-user-department" value={newUser.department} onChange={(e) => setNewUser({ ...newUser, department: e.target.value })} className="rounded-sm border-zinc-300" /></div>
              </div>
              <Button type="submit" disabled={creating} data-testid="submit-create-user" className="w-full rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white font-semibold">{creating ? "Creating..." : "Create User"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[#E4E4E7] shadow-none rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead><tr className="border-b border-[#E4E4E7] bg-[#F4F4F5]">
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Name</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Email</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Role</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Department</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Status</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Actions</th>
            </tr></thead>
            <tbody>
              {loading ? ["sk-1","sk-2","sk-3"].map((sk) => (
                <tr key={sk} className="border-b border-[#E4E4E7]">{["a","b","c","d","e","f"].map((col) => (<td key={col} className="px-3 py-2.5"><div className="h-4 bg-zinc-200 rounded-sm animate-pulse" /></td>))}</tr>
              )) : users.map((u) => (
                <tr key={u.id} className="border-b border-[#E4E4E7] hover:bg-zinc-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-2.5 font-medium text-[#09090B]">{u.name}</td>
                  <td className="px-3 py-2.5 text-[#52525B]">{u.email}</td>
                  <td className="px-3 py-2.5 capitalize text-[#52525B]">{u.role?.replace("_", " ")}</td>
                  <td className="px-3 py-2.5 text-[#52525B]">{u.department || "—"}</td>
                  <td className="px-3 py-2.5"><Badge variant={u.is_active ? "default" : "destructive"} className="rounded-sm text-[10px]">{u.is_active ? "Active" : "Inactive"}</Badge></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-7 rounded-sm text-xs gap-1" data-testid={`edit-user-${u.email}`}>
                        <Pencil size={12} /> Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setResetPwUser(u); setResetPwValue(""); }} className="h-7 rounded-sm text-xs gap-1" data-testid={`reset-pw-${u.email}`}>
                        <KeyRound size={12} /> Reset Password
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u.id, u.is_active)} className="h-7 rounded-sm text-xs gap-1" data-testid={`toggle-active-${u.email}`}>
                        {u.is_active ? <><UserX size={12} /> Deactivate</> : <><UserCheck size={12} /> Activate</>}
                      </Button>
                      {u.role !== "admin" && (
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(u)} className="h-7 rounded-sm text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.email}`}>
                          <Trash2 size={12} /> Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="rounded-sm border-[#E4E4E7] sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading font-bold">Edit User</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Name</Label>
                <Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Department</Label>
                <Input data-testid="edit-user-department" value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving} data-testid="save-edit-user" className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white">{editSaving ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="rounded-sm border-[#E4E4E7] sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading font-bold">Delete User</DialogTitle></DialogHeader>
          <p className="text-sm text-[#52525B]">Are you sure you want to delete <span className="font-semibold text-[#09090B]">{deleteConfirm?.name}</span> ({deleteConfirm?.email})? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleDelete} data-testid="confirm-delete-user" className="rounded-sm bg-red-600 hover:bg-red-700 text-white">Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwUser} onOpenChange={(open) => { if (!open) { setResetPwUser(null); setResetPwValue(""); } }}>
        <DialogContent className="rounded-sm border-[#E4E4E7] sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading font-bold">Reset Password</DialogTitle></DialogHeader>
          {resetPwUser && (
            <div className="space-y-3">
              <p className="text-sm text-[#52525B]">Set a new password for <span className="font-semibold text-[#09090B]">{resetPwUser.name}</span> ({resetPwUser.email}). They will be required to change it on next login.</p>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">New Password</Label>
                <Input type="password" data-testid="reset-pw-input" value={resetPwValue} onChange={(e) => setResetPwValue(e.target.value)} placeholder="Min 6 characters" className="rounded-sm border-zinc-300" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPwUser(null); setResetPwValue(""); }} className="rounded-sm">Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetPwSaving || resetPwValue.length < 6} data-testid="confirm-reset-pw" className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white">{resetPwSaving ? "Resetting..." : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
